/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The AI Assistant chat: whether the panel is open, and the conversation.
 *
 * Always mounted (App.js, like TelephonyProvider) and inert until the user may
 * use the assistant: authenticated, and useAiAssistantStatus says licensed,
 * seated and with credits. Every caller reads `enabled` before showing
 * anything, so a user without access never sees an AI button.
 *
 * The conversation lives here and only here. The server is stateless: each
 * message sends the text turns so far, and tool turns never leave it. The
 * conversation is dropped on `auth-token-updated`, so one tenant's or user's
 * chat can never show in another's session. A reply that arrives after that
 * reset is discarded rather than appended to the new session.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useAuth } from "auth/Auth";
import useAiAssistantStatus from "hooks/useAiAssistantStatus";
import { getApiErrorMessage } from "utils/apiErrors";

const AiAssistantContext = createContext(null);

// What a component outside the provider gets: nothing to show, nothing to do.
const INERT = {
  enabled: false,
  open: false,
  messages: [],
  sending: false,
  balance: null,
  toggle: () => {},
  close: () => {},
  reset: () => {},
  sendMessage: async () => {},
};

let nextId = 1;
const makeId = () => `m${nextId++}`;

export function AiAssistantProvider({ children }) {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const { canUseAiAssistant, status, refresh } = useAiAssistantStatus();
  const enabled = Boolean(isAuthReady && isAuthenticated && canUseAiAssistant);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  // Bumped on every reset; a reply for an older generation is dropped.
  const generation = useRef(0);

  const reset = useCallback(() => {
    generation.current += 1;
    setMessages([]);
    setSending(false);
  }, []);

  useEffect(() => {
    const onToken = () => {
      reset();
      setOpen(false);
    };
    window.addEventListener("auth-token-updated", onToken);
    return () => window.removeEventListener("auth-token-updated", onToken);
  }, [reset]);

  // Access can go away mid-session (seat revoked, credits ran out).
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  const toggle = useCallback(() => {
    if (enabled) setOpen((value) => !value);
  }, [enabled]);
  const close = useCallback(() => setOpen(false), []);

  const sendMessage = useCallback(
    async (text) => {
      const content = (text || "").trim();
      if (!content || !enabled || sending) return;
      const mine = generation.current;
      const userTurn = { id: makeId(), role: "user", content };
      // Only real turns are history; an error bubble is not something
      // either side said.
      const history = [...messages, userTurn]
        .filter((m) => !m.error && (m.role === "user" || m.role === "assistant"))
        .map((m) => ({ role: m.role, content: m.content }));
      setMessages((prev) => [...prev, userTurn]);
      setSending(true);
      try {
        const { data } = await axiosInstance.post("/ai-assistant/chat", { messages: history });
        if (mine !== generation.current) return;
        setMessages((prev) => [
          ...prev,
          {
            id: makeId(),
            role: "assistant",
            content: data?.reply || "",
            pendingActions: Array.isArray(data?.pending_actions) ? data.pending_actions : [],
            records: Array.isArray(data?.records) ? data.records : [],
          },
        ]);
        if (data?.out_of_credits) refresh();
      } catch (err) {
        if (mine !== generation.current) return;
        const statusCode = err?.response?.status;
        setMessages((prev) => [
          ...prev,
          {
            id: makeId(),
            role: "assistant",
            error: true,
            content: getApiErrorMessage(err, "The AI Assistant could not answer. Please try again."),
          },
        ]);
        // Out of credits, or the seat or licence went away: let every AI
        // entry point re-check and hide itself.
        if (statusCode === 402 || statusCode === 403) refresh();
      } finally {
        if (mine === generation.current) setSending(false);
      }
    },
    [axiosInstance, enabled, messages, refresh, sending]
  );

  const value = useMemo(
    () => ({
      enabled,
      open: enabled && open,
      messages,
      sending,
      // Only a system admin's status carries the balance.
      balance: typeof status?.balance === "number" ? status.balance : null,
      toggle,
      close,
      reset,
      sendMessage,
    }),
    [enabled, open, messages, sending, status, toggle, close, reset, sendMessage]
  );

  return <AiAssistantContext.Provider value={value}>{children}</AiAssistantContext.Provider>;
}

AiAssistantProvider.propTypes = {
  children: PropTypes.node,
};

export function useAiAssistant() {
  return useContext(AiAssistantContext) || INERT;
}
